import { createSelector } from '@reduxjs/toolkit';
import { format } from 'date-fns';

import { ScoreWeekly, Brand7dSummary } from '../scores/types';
import { scoreWeeklyFromDailyBrandSeries } from '../scores/utils/scoreWeeklyFromDailyBrandSeries';
import { scoreWeeklyFromDailyPromptSeries } from '../scores/utils/scoreWeeklyFromDailyPromptSeries';
import { scoreWeeklyFromDailyModelSeries } from '../scores/utils/scoreWeeklyFromDailyModelSeries';
import { RootState } from '../store';
import type { ScoreDailyAverage, ScoreOverview } from '../../types/scores';
  

export const selectBrandEveryDay7dSummaryForBrand = (state: RootState) =>
  state.scores.brand7dSummary.data;

export const selectBrandEveryDay7dSummaryForViewBrand = createSelector(
  (state: RootState) => state.brands.viewBrandId,
  (state: RootState) => state.scores.brand7dSummary.data,
  (viewBrandId, data): Brand7dSummary | null => {
    if (viewBrandId == null || data == null) return null;
    return data[viewBrandId] ?? null;
  },
);
export const selectDailyAverageBrandLoadingState = (state: RootState) => state.scores.dailyAverageBrand.loading;
export const selectDailyAveragePromptLoadingState = (state: RootState) => state.scores.dailyAveragePrompt.loading;
export const selectDailyAverageModelLoadingState = (state: RootState) => state.scores.dailyAverageModel.loading;
export const selectDailyAveragePromptError = (state: RootState) => state.scores.dailyAveragePrompt.error;
export const selectDailyAverageModelError = (state: RootState) => state.scores.dailyAverageModel.error;
export const selectScoresByPromptState = (state: RootState) => state.scores.scoresByPrompts.data;
export const selectScoresByModelState = (state: RootState) => state.scores.scoresByModels.data;
export const selectBrandRangeValuesState = (state: RootState) => state.scores.dailyAverageBrand.rangeValues;
export const selectPromptRangeValuesState = (state: RootState) => state.scores.dailyAveragePrompt.rangeValues;
export const selectModelRangeValuesState = (state: RootState) => state.scores.dailyAverageModel.rangeValues;


export const selectBrand7dSummaryPendingState = (state: RootState): boolean => {
  const s = state.scores.brand7dSummary;
  return s.loading === "loading" || (s.loading === "idle" && s.data === null);
};


export const selectOverviewScoresFromDailyLoadingState = (state: RootState) =>
  state.scores.overviewScoresFromDaily.loading;

export const selectOverviewTopModelLoadingState = (state: RootState) =>
  state.scores.overviewTopModel.loading;

export const selectOverviewTopModelLoadCompleteState = (state: RootState): boolean =>
  state.scores.overviewTopModel.loading === "success" ||
  state.scores.overviewTopModel.loading === "error";

export const selectOverviewTopModelState = (state: RootState) => state.scores.overviewTopModel.data;

export const selectWeeklyBrandScoreByViewBrandIdState = createSelector(
  (state: RootState) => state.brands.viewBrandId,
  (state: RootState) => state.scores.dailyAverageBrand.data,
  (state: RootState) => state.brands.brand,
  (state: RootState) => state.brands.competitors,
  (viewBrandId, daily, mainBrand, competitors): ScoreWeekly | null => {
    if (viewBrandId == null) return null;
    const item = daily?.[String(viewBrandId)];
    const name =
      mainBrand?.id === viewBrandId
        ? mainBrand.name
        : competitors?.find((c) => c.id === viewBrandId)?.name ?? '';
    return scoreWeeklyFromDailyBrandSeries(viewBrandId, item, name);
  },
);

export const selectLastUpdateTimeByViewBrandIdState = createSelector(
  (state: RootState) => state.brands.viewBrandId,
  (state: RootState) => state.scores.lastUpdates.data,
  (viewBrandId, lastUpdates): string | null => {
    if (viewBrandId == null) return null;
    return lastUpdates?.[viewBrandId] ?? null;
  },
);

export const selectOverviewScoreForViewBrand = createSelector(
  (state: RootState) => state.brands.viewBrandId,
  (state: RootState) => state.scores.overviewScoresFromDaily.data,
  (viewBrandId, data): ScoreOverview | null => {
    if (viewBrandId == null || data == null) return null;
    return data.find((s) => s.brand_id === viewBrandId) ?? null;
  },
);

export const selectWeeklyBrandScoresState = (id: number) =>
  createSelector(
    (state: RootState) => state.scores.dailyAverageBrand.data?.[String(id)],
    (state: RootState) => state.brands.brand,
    (state: RootState) => state.brands.competitors,
    (item, mainBrand, competitors): ScoreWeekly | null => {
      const name =
        mainBrand?.id === id ? mainBrand.name : competitors?.find((c) => c.id === id)?.name ?? '';
      return scoreWeeklyFromDailyBrandSeries(id, item, name);
    },
  );

/** Weekly prompt KPIs derived from `dailyAveragePrompt` (same pattern as `selectWeeklyBrandScoresState`). */
export const selectWeeklyPromptScoresState = (brandId: number, promptId: number) =>
  createSelector(
    (state: RootState) =>
      state.scores.dailyAveragePrompt.data?.[String(promptId)]?.[String(brandId)],
    (item): ScoreWeekly | null => scoreWeeklyFromDailyPromptSeries(brandId, promptId, item),
  );

/** Weekly model KPIs derived from `dailyAverageModel` (same pattern as brand / prompt). */
export const selectWeeklyModelScoresState = (brandId: number, modelId: number) =>
  createSelector(
    (state: RootState) =>
      state.scores.dailyAverageModel.data?.[String(modelId)]?.[String(brandId)],
    (item): ScoreWeekly | null => scoreWeeklyFromDailyModelSeries(brandId, modelId, item),
  );

export const selectDailyAverageBrandScoresState = (id: number) =>
  createSelector(
    (state: RootState) => state.scores.dailyAverageBrand.data,
    (scores): ScoreDailyAverage[] | undefined => scores?.[String(id)]?.scores,
  );

export const selectDailyAveragePromptScoresState = (brandId: number, promptId: number) =>
  createSelector(
    (state: RootState) => state.scores.dailyAveragePrompt.data,
    (scores) => scores?.[String(promptId)]?.[String(brandId)]?.scores,
  );

export const selectDailyAverageModelScoresState = (brandId: number, modelId: number) =>
  createSelector(
    (state: RootState) => state.scores.dailyAverageModel.data,
    (scores) => scores?.[String(modelId)]?.[String(brandId)]?.scores,
  );

export const selectDailyAveragePromptScoresByDateState = (
  brandId: number,
  promptId: number,
  date: string | undefined,
) =>
  createSelector(
    (state: RootState) => state.scores.dailyAveragePrompt.data,
    (scores): number | null => {
      if (!scores || !date || !brandId || !promptId) return null;
      const item = scores[String(promptId)]?.[String(brandId)]?.scores?.find(
        (score) => format(new Date(score.created_at), 'yyyy-MM-dd') === date,
      );
      return item?.visibility ?? null;
    },
  );

export const selectDailyAverageModelScoresByDateState = (
  brandId: number,
  modelId: number,
  date: string | undefined,
) =>
  createSelector(
    (state: RootState) => state.scores.dailyAverageModel.data,
    (scores): number | null => {
      if (!scores || !date || !brandId || !modelId) return null;
      const item = scores[String(modelId)]?.[String(brandId)]?.scores?.find(
        (score) => format(new Date(score.created_at), 'yyyy-MM-dd') === date,
      );
      return item?.visibility ?? null;
    },
  );

export const selectDailyAverageBrandScoresByDateState = (
  id: number,
  date: string | undefined,
) =>
  createSelector(
    (state: RootState) => state.scores.dailyAverageBrand.data,
    (scores): number | null => {
      if (!scores || !date || !id) return null;
      const item = scores[String(id)]?.scores?.find(
        (score) => format(new Date(score.created_at), 'yyyy-MM-dd') === date,
      );
      return item?.visibility ?? null;
    },
  );

export const selectLastUpdateTimeState = (id: number) =>
  createSelector(
    (state: RootState) => state.scores.lastUpdates.data,
    (lastUpdates) => lastUpdates?.[String(id)],
  );

export const selectOverviewScoresBrandsByDateState = createSelector(
  (state: RootState) => state.scores.overviewScoresFromDaily.data,
  (state: RootState) => state.scores.overviewScoresFromDaily.loading,
  (data, loading): ScoreOverview[] | null => {
    if (loading === "loading" || data === null) return null;
    return data;
  },
);